"use client";

import { useCartStore } from "@/lib/cart-store";
import { MemberAvatarImg } from "@/components/site/MemberAvatar";

import { RESORTS, resortHubHref } from "./resorts";
import { openMountainSelect } from "./MountainSelectLayer";

// Phase-1b PORT: the live snowbus.com CONTENT/INFO header variant (MUI AppBar) as a real, hydrated
// component — the distinct header these pages use (vs home's `homeHeader`). The live site renders a
// DIFFERENT header DOM per breakpoint (desktop `css-166albc` big "Select your mountain" pill; mobile
// `css-a9gwjv` text+search-icon), so this component reproduces BOTH, selected by the `mobile` prop that
// page.tsx derives from the request UA. Faithful F26 reproduction (no unification — Phase-2 design call).
// The "Select your mountain" control is the real interaction (opens the 30-mountain list); cart + profile
// are systems not yet ported (inert). Hidden resort-sitemap SEO nav preserved for link equity.
const SELECT_BTN: React.CSSProperties = {
  textTransform: "capitalize",
  margin: "auto",
  display: "flex",
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 500,
  padding: "10px 30px",
  width: "186px",
  height: "60px",
  borderRadius: "999px",
  color: "#fff",
  border: "1px solid #f15a22",
  backgroundColor: "#f15a22",
  transition: "all 0.3s ease-in-out",
};

export default function SiteHeaderContent({
  mobile = false,
}: {
  mobile?: boolean;
}) {
  const setAuthOpen = useCartStore((s) => s.setAuthOpen); // W6 — profile icon opens the login popup

  // S26 · A3 — the local change-mountain menu is REPLACED by the one site-wide R1a selector.
  //
  // With it goes the whole S19 anchoring apparatus, which existed because this menu was `position:fixed`
  // against a measured rect and had already once opened at y = -110 (off-screen, a dead control) when
  // F122 changed the header's flex layout underneath it. A full-viewport overlay has no anchor to get
  // wrong, so that failure mode is designed out rather than re-fixed.
  //
  // The S26 · A2 audit found this toggle DEAD on /faq, /trips and /contact-us for a second, independent
  // reason: `ContentHudLayer` portals the mountain badge INTO the pill, and a React portal's click
  // bubbles through the REACT tree (the layer), never reaching this button's `onClick`. The badge covers
  // the pill's centre, so the ordinary click hit only the portal. ContentHudLayer now binds a NATIVE
  // listener to the pill as well — native listeners bubble through the DOM, so the badge click reaches it.
  const sitemap = (
    <nav
      aria-label="Resorts sitemap"
      style={{ display: "none" }}
      className="resort-sitemap"
    >
      <ul>
        {RESORTS.map((r) => (
          <li key={r.href}>
            <a href={r.href}>{r.label}</a>
          </li>
        ))}
        {/* S7 (W5/P2 orphans) — the bare mountain LANDINGS + the two content pages (general-information,
            privacy-policy) were unreachable-from-/ (live reaches landings only via the JS funnel). Content
            pages ARE reachable from home (footer FAQ/Contact/Terms), so this hidden SEO nav gives each a real
            inbound link (link equity) → landings leave the orphan set and cascade to their sub-pages via the
            now-real HUD nav. display:none → pixel gate untouched. */}
        {/* S15 — this list already needed "the bare landing for a RESORTS entry" and built the slug
            inline. That is exactly `resortHubHref`, so it now shares the one mapping (byte-identical
            output for all 30; the link-graph gate proves it). */}
        {RESORTS.map((r) => {
          const landing = resortHubHref(r.href);
          return (
            <li key={landing}>
              <a href={landing}>{r.label}</a>
            </li>
          );
        })}
        <li>
          <a href="/general-information">General Information</a>
        </li>
        <li>
          <a href="/privacy-policy">Privacy Policy</a>
        </li>
      </ul>
    </nav>
  );
  const cartButton = (
    <button
      className="MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit MuiIconButton-sizeMedium css-1sxc1sc"
      type="button"
      aria-label="Open cart"
    >
      <span className="MuiBadge-root css-oqf3hq">
        <img
          alt=""
          width={32}
          height={24}
          style={{
            color: "transparent",
            display: "block",
            width: "32px",
            height: "24px",
            transform: "translateY(-1px)",
          }}
          src="/_next/static/media/openCartIcon.f3aa487e.svg"
        />
        <span className="MuiBadge-badge MuiBadge-standard MuiBadge-invisible MuiBadge-anchorOriginTopRight MuiBadge-anchorOriginTopRightRectangular MuiBadge-overlapRectangular MuiBadge-colorError css-c6vaoj">
          0
        </span>
      </span>
      <span className="MuiTouchRipple-root css-w0pj6f" />
    </button>
  );
  const profileButton = (
    <button
      className="MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit MuiIconButton-sizeMedium css-1deacqj"
      type="button"
      data-behavior="signin-open"
      aria-label="Sign in"
      onClick={() => setAuthOpen(true)}
    >
      <MemberAvatarImg alt="Profile" width={32} height={32} style={{ color: "transparent" }} fallback="/_next/static/media/profileIcon.4bf36b73.svg" />
      <span className="MuiTouchRipple-root css-w0pj6f" />
    </button>
  );

  if (mobile) {
    return (
      <header className="MuiPaper-root MuiPaper-elevation MuiPaper-elevation0 MuiAppBar-root MuiAppBar-colorDefault MuiAppBar-positionSticky css-1awevnq">
        <div className="MuiToolbar-root MuiToolbar-gutters MuiToolbar-regular css-blq5xp">
          <a aria-label="Home" href="/">
            <img
              alt="Logo"
              width={49}
              height={56}
              style={{
                color: "transparent",
                height: "40px",
                width: "auto",
                cursor: "pointer",
              }}
              src="/_next/static/media/headerLogoNew2.713156d5.svg"
            />
          </a>
          <div
            data-behavior="nav-toggle"
            role="button"
            aria-haspopup="dialog"
            onClick={() => openMountainSelect(null)}
            className="MuiBox-root css-a9gwjv"
            style={{ cursor: "pointer" }}
          >
            <p className="MuiTypography-root MuiTypography-body1 css-28uxga">
              Select your mountain
            </p>
            <img
              alt="Search"
              width={16}
              height={16}
              style={{ color: "transparent" }}
              src="/_mirror/_next/static/media/blueIcon.f4019d62.svg"
            />
          </div>
          {sitemap}
          <div className="MuiBox-root css-4mocpf">
            {cartButton}
            {profileButton}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="MuiPaper-root MuiPaper-elevation MuiPaper-elevation0 MuiAppBar-root MuiAppBar-colorDefault MuiAppBar-positionSticky css-1j5e5rn">
      <div className="MuiToolbar-root MuiToolbar-gutters MuiToolbar-regular css-2u379j">
        <div className="MuiBox-root css-166albc">
          <a aria-label="Home" href="/">
            <img
              alt="Logo"
              width={49}
              height={56}
              style={{
                color: "transparent",
                height: "40px",
                width: "auto",
                cursor: "pointer",
              }}
              src="/_next/static/media/headerLogoNew2.713156d5.svg"
            />
          </a>
        </div>
        <div className="MuiBox-root css-fmy3cc">
          <div className="MuiBox-root css-b3n6tb">
            <div className="MuiBox-root css-6su6fj">
              <button
                className="MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary MuiButton-sizeMedium MuiButton-containedSizeMedium MuiButton-colorPrimary StandardButton_btnPrimary__EZrWc css-1hw9j7s"
                type="button"
                data-behavior="nav-toggle"
                // S26 · A3 — opens the ONE site-wide R1a selector. ContentHudLayer ALSO binds a NATIVE
                // listener to this button, because it portals the mountain badge INTO it and a portal's
                // click does not bubble through the REACT tree to the node it is portaled into — which is
                // why this toggle was dead on /faq, /trips and /contact-us (S26 · A2).
                aria-haspopup="dialog"
                onClick={() => openMountainSelect(null)}
                style={SELECT_BTN}
              >
                Select your mountain
              </button>
              {sitemap}
            </div>
          </div>
          <div className="MuiBox-root css-1cqx21f">{cartButton}</div>
        </div>
        <div className="MuiBox-root css-ss5hdk">{profileButton}</div>
      </div>
    </header>
  );
}
